/**
 * UI Store
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

type SidebarContent = 'filters' | 'search' | 'entity' | 'favorites' | 'stats' | 'history' | 'export' | 'reports';
type MobileSheetHeight = 'min' | 'half' | 'full';

interface ModalState {
  login: boolean;
  register: boolean;
  forgotPassword: boolean;
  relationsGraph: boolean;
  photoViewer: boolean;
  intersect: boolean;
  export: boolean;
  createNote: boolean;
  uploadPhoto: boolean;
  createJob: boolean;
}

interface UIState {
  // Sidebar
  sidebarOpen: boolean;
  sidebarContent: SidebarContent;
  sidebarWidth: number;
  
  // Mobile sheet
  mobileSheetOpen: boolean;
  mobileSheetHeight: MobileSheetHeight;
  
  // Modals
  modals: ModalState;
  
  // Active tab in entity cards
  activeTab: string;
  
  // Photo viewer state
  photoViewerIndex: number;
  photoViewerImages: string[];
  
  // Loading states
  isMapLoading: boolean;
  isDataLoading: boolean;
  
  // Sidebar actions
  toggleSidebar: () => void;
  openSidebar: () => void;
  closeSidebar: () => void;
  setSidebarContent: (content: SidebarContent) => void;
  setSidebarWidth: (width: number) => void;
  
  // Mobile sheet actions
  openMobileSheet: () => void;
  closeMobileSheet: () => void;
  setMobileSheetHeight: (height: MobileSheetHeight) => void;
  
  // Modal actions
  openModal: (modal: keyof ModalState) => void;
  closeModal: (modal: keyof ModalState) => void;
  closeAllModals: () => void;
  
  // Tab actions
  setActiveTab: (tab: string) => void;
  
  // Photo viewer actions
  openPhotoViewer: (images: string[], index: number) => void;
  closePhotoViewer: () => void;
  setPhotoViewerIndex: (index: number) => void;
  
  // Loading actions
  setMapLoading: (loading: boolean) => void;
  setDataLoading: (loading: boolean) => void;
}

const defaultModals: ModalState = {
  login: false,
  register: false,
  forgotPassword: false,
  relationsGraph: false,
  photoViewer: false,
  intersect: false,
  export: false,
  createNote: false,
  uploadPhoto: false,
  createJob: false,
};

export const useUIStore = create<UIState>()(
  devtools(
    (set) => ({
      // Initial state
      sidebarOpen: true,
      sidebarContent: 'filters',
      sidebarWidth: 400,
      
      mobileSheetOpen: false,
      mobileSheetHeight: 'min',
      
      modals: defaultModals,
      
      activeTab: 'info',
      
      photoViewerIndex: 0,
      photoViewerImages: [],
      
      isMapLoading: true,
      isDataLoading: false,
      
      // Sidebar actions
      toggleSidebar: () => set((state) => ({
        sidebarOpen: !state.sidebarOpen,
      })),
      
      openSidebar: () => set({ sidebarOpen: true }),
      closeSidebar: () => set({ sidebarOpen: false }),
      
      setSidebarContent: (content) => set({
        sidebarContent: content,
        sidebarOpen: true,
      }),
      
      setSidebarWidth: (width) => set({
        sidebarWidth: Math.max(300, Math.min(600, width)),
      }),
      
      // Mobile sheet actions
      openMobileSheet: () => set({
        mobileSheetOpen: true,
        mobileSheetHeight: 'half',
      }),
      
      closeMobileSheet: () => set({
        mobileSheetOpen: false,
        mobileSheetHeight: 'min',
      }),
      
      setMobileSheetHeight: (height) => set({
        mobileSheetHeight: height,
        mobileSheetOpen: height !== 'min',
      }),
      
      // Modal actions
      openModal: (modal) => set((state) => ({
        modals: {
          ...state.modals,
          [modal]: true,
        },
      })),
      
      closeModal: (modal) => set((state) => ({
        modals: {
          ...state.modals,
          [modal]: false,
        },
      })),
      
      closeAllModals: () => set({
        modals: defaultModals,
      }),
      
      // Tab actions
      setActiveTab: (tab) => set({ activeTab: tab }),
      
      // Photo viewer actions
      openPhotoViewer: (images, index) => set({
        photoViewerImages: images,
        photoViewerIndex: index,
        modals: { ...defaultModals, photoViewer: true },
      }),
      
      closePhotoViewer: () => set((state) => ({
        modals: { ...state.modals, photoViewer: false },
        photoViewerImages: [],
        photoViewerIndex: 0,
      })),
      
      setPhotoViewerIndex: (index) => set({ photoViewerIndex: index }),
      
      // Loading actions
      setMapLoading: (loading) => set({ isMapLoading: loading }),
      setDataLoading: (loading) => set({ isDataLoading: loading }),
    }),
    { name: 'UIStore' }
  )
);

// Selectors
export const selectSidebarOpen = (state: UIState) => state.sidebarOpen;
export const selectSidebarContent = (state: UIState) => state.sidebarContent;
export const selectModals = (state: UIState) => state.modals;
export const selectActiveTab = (state: UIState) => state.activeTab;
